import { Box, Paper, styled } from '@mui/material'
import { Calendar } from 'react-big-calendar'
// @ts-ignore
import 'react-big-calendar/lib/css/react-big-calendar.css'
import { MUIThemeColors } from '../../mui-theme'
import { getThemeColor } from '../../utils'
import { CALENDAR_MIN_WIDTH } from './calendarLayout'

// Toolbar row above the calendar: type filters (left), service-frequency + search (right).
export const CalendarToolbar = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius * 3,
  border: '1px solid rgba(100, 116, 139, 0.14)',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
  display: 'flex',
  flexWrap: 'wrap',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}))

// Surface that hosts the calendar grid (relative for the loading bar overlay).
export const CalendarSurface = styled(Paper)(({ theme }) => ({
  position: 'relative',
  overflow: 'hidden',
  padding: theme.spacing(1.5),
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2.5),
  },
  borderRadius: theme.shape.borderRadius * 3,
  border: '1px solid rgba(100, 116, 139, 0.14)',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.05)',
}))

export const CalendarScroller = styled(Box)({
  width: '100%',
  overflowX: 'auto',
  paddingBottom: '10px',
})

export const EventContentBox = styled(Box)({ display: 'block', width: '100%', minWidth: 0, overflow: 'hidden' })

interface EventDetailsProps {
  maxTextWidth?: number
}

// Event label content (title • locality). Text inherits the deep, per-type colour set on
// the pill via eventPropGetter, so it stays readable on the soft tinted background.
export const EventDetails = styled(Box, {
  shouldForwardProp: prop => prop !== 'maxTextWidth',
})<EventDetailsProps>(({ maxTextWidth }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  gap: '5px',
  width: '100%',
  maxWidth: maxTextWidth ? `${maxTextWidth}px` : '100%',
  minWidth: 0,
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  '.event-title, .event-locality': {
    minWidth: 0,
    flexShrink: 1,
    overflow: 'hidden',
    textAlign: 'left',
    margin: 0,
    '& > div': { minWidth: 0, maxWidth: '100%' },
  },
  '.event-title': { fontWeight: 600, fontSize: '11.5px', lineHeight: 1.25, color: 'inherit' },
  '.event-separator': { fontSize: '10px', color: 'currentColor', opacity: 0.45, flexShrink: 0 },
  '.event-locality': {
    fontSize: '10px',
    lineHeight: 1.25,
    color: 'currentColor',
    opacity: 0.7,
    fontWeight: 600,
    letterSpacing: '0.3px',
    textTransform: 'uppercase',
  },
}))

export const StyledCalendar = styled(Calendar)(({ theme }) => {
  const primaryMain = getThemeColor(theme, MUIThemeColors.primaryMain)
  const primaryDark = getThemeColor(theme, MUIThemeColors.primaryDark)
  const white = getThemeColor(theme, MUIThemeColors.white)

  const ink = 'rgba(15, 23, 42, 0.88)' // slate-900-ish primary text
  const muted = 'rgba(71, 85, 105, 0.65)' // slate-500 muted
  const hairline = 'rgba(100, 116, 139, 0.14)' // subtle grid lines
  const surfaceHover = 'rgba(37, 99, 235, 0.04)'
  const todayTint = 'rgba(37, 99, 235, 0.06)'

  return {
    height: 'calc(100vh - 240px)',
    minHeight: '560px',
    backgroundColor: white,
    fontFamily: theme.typography.fontFamily,
    position: 'relative',
    overflow: 'visible',

    /* ---------- Toolbar (month nav + view switch) ---------- */
    '& .rbc-toolbar': {
      marginBottom: '16px',
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      flexWrap: 'wrap',
      gap: '8px',

      '& .rbc-toolbar-label': {
        fontSize: '17px',
        fontWeight: 700,
        color: ink,
        letterSpacing: '0.2px',
        textTransform: 'capitalize',
      },

      '& button': {
        color: muted,
        border: '1px solid transparent',
        backgroundColor: 'transparent',
        padding: '7px 14px',
        borderRadius: '8px',
        fontWeight: 600,
        fontSize: '13.5px',
        cursor: 'pointer',
        transition: 'background-color .15s ease, color .15s ease, border-color .15s ease',

        '&:hover': {
          backgroundColor: surfaceHover,
          color: primaryDark,
        },
        '&:active, &:focus': {
          backgroundColor: surfaceHover,
          color: primaryDark,
        },
      },

      /* segmented control look for grouped buttons */
      '& .rbc-btn-group': {
        display: 'inline-flex',
        gap: '4px',
        backgroundColor: 'rgba(100, 116, 139, 0.08)',
        borderRadius: '10px',
        padding: '3px',

        '& button': {
          padding: '6px 14px',
        },
        '& button.rbc-active': {
          backgroundColor: white,
          color: primaryDark,
          boxShadow: '0 1px 2px rgba(15, 23, 42, 0.12)',
        },
      },
    },

    /* ---------- Month frame ---------- */
    '& .rbc-month-view': {
      border: `1px solid ${hairline}`,
      borderRadius: '14px',
      overflow: 'hidden',
      position: 'relative',
      backgroundColor: white,
      minWidth: `${CALENDAR_MIN_WIDTH}px`,
    },

    /* ---------- Weekday header ---------- */
    '& .rbc-month-header': {
      backgroundColor: 'rgba(248, 250, 252, 0.8)',
    },
    '& .rbc-header': {
      padding: '12px 8px',
      textAlign: 'center',
      fontSize: '11.5px',
      fontWeight: 700,
      color: muted,
      borderBottom: `1px solid ${hairline}`,
      borderLeft: 'none',
      textTransform: 'uppercase',
      letterSpacing: '0.8px',
    },
    '& .rbc-header + .rbc-header': {
      borderLeft: 'none',
    },

    /* ---------- Cells / grid ---------- */
    '& .rbc-month-row': {
      borderBottom: `1px solid ${hairline}`,
      minHeight: '116px',
      overflow: 'visible',
    },
    '& .rbc-day-bg': {
      borderLeft: `1px solid ${hairline}`,
      transition: 'background-color .15s ease',
      '&:hover': { backgroundColor: surfaceHover },
    },
    '& .rbc-today': {
      backgroundColor: todayTint,
    },
    '& .rbc-off-range-bg': {
      backgroundColor: 'rgba(241, 245, 249, 0.55)',
    },
    '& .rbc-off-range .rbc-button-link': {
      color: 'rgba(148, 163, 184, 0.8)',
    },

    /* ---------- Date numbers ---------- */
    '& .rbc-date-cell': {
      padding: '6px 8px 0',
      textAlign: 'right',
      fontSize: '13px',
      color: muted,
      '& .rbc-button-link': {
        color: muted,
        fontWeight: 600,
        fontSize: '13px',
        padding: '2px',
        borderRadius: '999px',
        minWidth: '26px',
        height: '26px',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background-color .15s ease, color .15s ease',
        '&:hover': { backgroundColor: surfaceHover, color: primaryDark },
      },
    },
    /* Today's date number → filled primary badge */
    '& .rbc-now .rbc-button-link': {
      backgroundColor: primaryMain,
      color: white,
      '&:hover': { backgroundColor: primaryDark, color: white },
    },

    '& .rbc-row-content': { position: 'relative', overflow: 'visible' },
    '& .rbc-row-segment': { padding: '1px 6px' },

    /* ---------- Event pills ---------- */
    '& .rbc-event': {
      borderRadius: '7px',
      padding: '3px 8px',
      fontSize: '11.5px',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'transform .12s ease, box-shadow .12s ease',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minHeight: '22px',
      lineHeight: '1.35',
      outline: 'none',

      '&:hover': {
        transform: 'translateY(-1px)',
        boxShadow: '0 3px 8px rgba(15, 23, 42, 0.12)',
        zIndex: 10,
      },
      '&:focus-visible': {
        boxShadow: `0 0 0 2px ${white}, 0 0 0 3px ${primaryMain}`,
      },
    },
    '& .rbc-event.rbc-selected': {
      boxShadow: `0 0 0 2px ${white}, 0 0 0 4px ${primaryMain}`,
    },
    '& .rbc-event-label': { display: 'none' },
    '& .rbc-event-content': { width: '100%', overflow: 'hidden' },

    /* ---------- "+N more" pill ---------- */
    '& .rbc-show-more': {
      backgroundColor: 'transparent',
      color: primaryMain,
      padding: '2px 8px',
      borderRadius: '6px',
      fontSize: '11px',
      fontWeight: 700,
      marginTop: '2px',
      display: 'inline-block',
      cursor: 'pointer',
      transition: 'background-color .15s ease',
      '&:hover': { backgroundColor: surfaceHover, textDecoration: 'none' },
    },

    /* ---------- Overlay popup (when clicking +N more) ---------- */
    '& .rbc-overlay': {
      borderRadius: '12px',
      border: `1px solid ${hairline}`,
      boxShadow: '0 12px 32px rgba(15, 23, 42, 0.18)',
      padding: '10px',
    },
    '& .rbc-overlay-header': {
      borderBottom: `1px solid ${hairline}`,
      fontWeight: 700,
      color: ink,
      paddingBottom: '8px',
      marginBottom: '6px',
    },

    /* ---------- Day view ---------- */
    '& .rbc-time-view': {
      border: `1px solid ${hairline}`,
      borderRadius: '14px',
      overflow: 'hidden',
      minWidth: `${CALENDAR_MIN_WIDTH}px`,
    },
    '& .rbc-time-header, & .rbc-time-content': { borderColor: hairline },
    '& .rbc-time-slot': { borderColor: `${hairline}` },
    '& .rbc-current-time-indicator': { backgroundColor: primaryMain, height: '2px' },
  }
})
